import { useEffect, useState } from 'react';
import type { DatePickerProps } from 'antd';
import { Button, DatePicker, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistorySettlementsTableStore } from 'store/historySettlementsTable';
import { useHistorySettlementsTableFiltersStore } from 'store/historySettlementsTableFilters';
import { useMerchantsStore } from 'store/merchants';
import MultiSelect from 'ui/controls/MultiSelect';
import { ExportButton } from 'ui/ExportButton/ExportButton';
import { dateHelpers } from 'utils/helpers/dateHelpers';
import { predicates } from 'utils/helpers/predicates';
import type { TComponent } from 'utils/types/component';

import omitBy from 'lodash/omitBy';

import { useDateValidation } from 'components/utils/hooks/useDateValidation';

import styles from './Filters.module.scss';
import { filtersHelpers } from './helpers';
import type { IFilterForm } from './types';

const INITIAL_FILTERS: IFilterForm = {
    startDate: '',
    endDate: '',
    currencyCodes: [],
    types: [],
    merchantIdentities: [],
};

interface IProps {
    onExport: () => void;
}

export const Filters: TComponent<IProps> = (props) => {
    const { onExport } = props;

    const { t } = useTranslation();

    const { isValidDateRange } = useDateValidation();

    const [form] = Form.useForm<IFilterForm>();
    const [formData, setFormData] = useState<IFilterForm>(INITIAL_FILTERS);

    const params = useHistorySettlementsTableFiltersStore('params');
    const clearBody = useHistorySettlementsTableFiltersStore('clearBody');
    const setBody = useHistorySettlementsTableFiltersStore('setBody');
    const setParams = useHistorySettlementsTableFiltersStore('setParams');
    const getTypesOptions =
        useHistorySettlementsTableFiltersStore('getTypesOptions');
    const getMany = useHistorySettlementsTableStore('getTable');
    const currenciesOptions = useMerchantsStore('currencyFilter');
    const merchantsOptions = useMerchantsStore('merchantNameFilter');

    const maxDate = dateHelpers.getCurrentDate();

    useEffect(() => {
        return () => {
            form.resetFields();

            setFormData(INITIAL_FILTERS);

            clearBody();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initStartDate = formData.startDate
        ? dateHelpers.formatDays(formData.startDate)
        : null;
    const initEndDate = formData.endDate
        ? dateHelpers.formatDays(formData.endDate)
        : null;

    const typeOptions = getTypesOptions(t);

    const handleClearFilters = () => {
        form.resetFields();

        setFormData(INITIAL_FILTERS);

        clearBody();

        getMany({}, params);
    };

    const onSubmit = () => {
        const startDate = formData.startDate;
        const endDate = formData.endDate;

        if (!isValidDateRange(startDate, endDate)) {
            return;
        }

        const enhanceData = omitBy(
            filtersHelpers.getFiltersData(formData),
            (field) =>
                predicates.isUndefined(field) ||
                predicates.isEmptyString(field) ||
                predicates.isEmptyArray(field),
        );

        const newParams = { ...params, page: 0 };

        setBody(enhanceData);
        setParams(newParams);

        getMany(enhanceData, newParams);
    };

    const handleChangeMerchants = (
        merchantIdentities: IFilterForm['merchantIdentities'],
    ) => {
        setFormData((data) => ({
            ...data,
            merchantIdentities,
        }));
    };

    const handleChangeStartDate: DatePickerProps['onChange'] = async (
        _,
        startDate,
    ) => {
        if (Array.isArray(startDate)) {
            return;
        }

        setFormData((data) => ({
            ...data,
            startDate,
        }));
    };

    const handleChangeEndDate: DatePickerProps['onChange'] = async (
        _,
        endDate,
    ) => {
        if (Array.isArray(endDate)) {
            return;
        }

        setFormData((data) => ({
            ...data,
            endDate,
        }));
    };

    const handleChangeCurrency = (
        currencyCodes: IFilterForm['currencyCodes'],
    ) => {
        setFormData((data) => ({
            ...data,
            currencyCodes,
        }));
    };

    const handleChangeType = (types: IFilterForm['types']) => {
        setFormData((data) => ({
            ...data,
            types,
        }));
    };

    return (
        <Form form={form} onFinish={onSubmit} className={styles.leftControls}>
            <div className={styles.filtersContainer}>
                <MultiSelect
                    value={formData.currencyCodes}
                    onChange={handleChangeCurrency}
                    placeholder={t('common.form.currency')}
                    options={currenciesOptions}
                    maxTagCount='responsive'
                    className={styles.multiSelect}
                />
                <MultiSelect
                    value={formData.types}
                    onChange={handleChangeType}
                    placeholder={t('common.form.type')}
                    options={typeOptions}
                    maxTagCount='responsive'
                    className={styles.multiSelect}
                />
                <MultiSelect
                    value={formData.merchantIdentities}
                    onChange={handleChangeMerchants}
                    placeholder={t('common.form.merchant')}
                    options={merchantsOptions}
                    maxTagCount='responsive'
                    className={styles.multiSelect}
                />
            </div>
            <div className={styles.filtersContainer}>
                <DatePicker
                    showTime
                    value={initStartDate}
                    onChange={handleChangeStartDate}
                    placeholder={t('common.form.dateFrom')}
                    className={styles.datePicker}
                    maxDate={maxDate}
                />
                <DatePicker
                    showTime
                    value={initEndDate}
                    onChange={handleChangeEndDate}
                    placeholder={t('common.form.dateTo')}
                    className={styles.datePicker}
                    maxDate={maxDate}
                />
            </div>
            <div className={styles.controlsContainer}>
                <div className={styles.buttonsContainer}>
                    <Button type='default' onClick={handleClearFilters}>
                        {t('common.buttons.clear')}
                    </Button>
                    <Button type='primary' htmlType='submit'>
                        {t('common.buttons.apply')}
                    </Button>
                </div>

                <div className={styles.rightControls}>
                    <ExportButton onClick={onExport} />
                </div>
            </div>
        </Form>
    );
};
